/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import { AuthPieceProps } from "aws-amplify-react/props";
import React, { useState } from "react";
import { Button, Grid, Message, Transition } from "semantic-ui-react";
import SignIn from "../../SignIn";
import styles from "./LandingForm.module.css";
import appShieldLogo from "./AppShieldLogo.svg";
import africaCom from "./AfricaCom.png";
import stevie from "./Stevie.png";
import gartner from "./Gartner.png";
import ForgotPassword from "../../ForgotPassword";
import ConfirmSignUp from "../../ConfirmSignUp";
import VerifyContact from "../../VerifyContact";
import RequireNewPassword from "../../RequireNewPassword";
import { useTranslation } from "react-i18next";

const LandingForm: React.FC<AuthPieceProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<null | string | React.ReactNode>(null);
  const authData = props.authData;
  const messageRef = React.useRef<any>(null);
  const noTrial = sessionStorage.getItem("AsNoTrial") === "true";
  const { t } = useTranslation("common");

  React.useEffect(() => {
    if (message && messageRef.current?.scrollIntoView) {
      messageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [message]);

  const learnColumn = () => {
    return (
      <Grid.Column width="7" stretched id={styles.learnColumn}>
        <div
          className={
            styles.learnContent
          }
        >
          <div className={styles.logo}>
            <Transition transitionOnMount animation="fade" duration="666">
              <img src={appShieldLogo} alt="Verimatrix App Shield" />
            </Transition>
          </div>

          {noTrial && <div style={{ height: "22rem" }}></div>}

          <div className={styles.learnColumnBottom}>
            <Transition transitionOnMount animation="fade right" duration="666">
              <div className={styles.awardText}>{t("award_winning")}</div>
            </Transition>

            <div className={styles.awards}>
              <div className={styles.awardLogo}>
                <img src={gartner} width="125" alt="Gartner Award" />
              </div>
              <div className={styles.awardLogo}>
                <img src={stevie} width="95" alt="Stevie Award" />
              </div>
              <div className={styles.awardLogo}>
                <img src={africaCom} width="125" alt="AfricaCom Award" />
              </div>
            </div>

            <Transition transitionOnMount animation="fade left" duration="666">
              <a
                href="https://www.verimatrix.com/products/app-shield/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginTop: "2rem", outline: "none" }}
              >
                <Button
                  className={`${styles.learnButton} ${styles.directModeButton}`}
                >
                  {t("learn_more")}
                </Button>
              </a>
            </Transition>
          </div>
        </div>
      </Grid.Column>
    );
  };

  const loginColumn = () => {
    const { authState } = props;

    const formTitle = () => {
      switch (authState) {
        case "forgotPassword":
        case "requireNewPassword":
          return t("reset_password");
        case "verifyContact":
          return t("verify_contact");
        case "confirmSignUp":
          return t("confirm_email");
        default:
          return t("log_in");
      }
    };

    const showMessage =
      authData && authData.message ? authData.message : message;

    return (
      <Grid.Column
        width="9"
        stretched
        verticalAlign="top"
        textAlign="center"
        id={styles.loginColumn}
      >
        <div className={styles.formContainer} ref={messageRef}>
          <div className={styles.formTitle}>
            <div className={styles.titleText}>{formTitle()}</div>
          </div>

          {(() => {
            switch (authState) {
              case "forgotPassword":
                return (
                  <ForgotPassword
                    {...props}
                    setMessage={setMessage}
                    loading={loading}
                    setLoading={setLoading}
                  />
                );
              case "confirmSignUp":
                return (
                  <ConfirmSignUp
                    {...props}
                    setMessage={setMessage}
                    loading={loading}
                    setLoading={setLoading}
                  />
                );
              case "verifyContact":
                return (
                  <VerifyContact
                    {...props}
                    setMessage={setMessage}
                    loading={loading}
                    setLoading={setLoading}
                  />
                );
              case "requireNewPassword":
                return (
                  <RequireNewPassword
                    {...props}
                    setMessage={setMessage}
                    loading={loading}
                    setLoading={setLoading}
                  />
                );

              default:
                return (
                  <SignIn
                    {...props}
                    setMessage={setMessage}
                    loading={loading}
                    setLoading={setLoading}
                  />
                );
            }
          })()}

          <Transition
            visible={showMessage}
            transitionOnMount
            animation="fade down"
            duration="333"
          >
            <Message className={styles.formMessage} content={showMessage} />
          </Transition>
        </div>
      </Grid.Column>
    );
  };

  return (
    <Grid
      stackable
      columns="2"
      style={{ margin: 0, backgroundColor: "#fbfbfb" }}
    >
      {learnColumn()}
      {loginColumn()}
    </Grid>
  );
};

export default LandingForm;
